import { memo, useEffect, useMemo, useState } from 'react'
import { styled, useTheme } from '@mui/material'
import { useIntl } from 'react-intl'

import { ContextMenuItem, ContextMenuIcon } from 'features/common/ui/ContextMenu'
import useMapSharingInfo from 'features/maps/state/useMapSharingInfo'

import CardBasicInformation from 'features/common/ui/Cards/CardBasicInformation'
import { DemoMap, Map, MapActions } from 'features/maps/types'

import useDuplicateMap from 'features/maps/state/useDuplicateMap'
import MapCardDialogWrapper from './MapCardDialogWrapper'
import { useAccountQuotas } from 'features/quotas/ui/hooks'
import { useHardQuotaModalController } from 'features/quotas/ui/WouldExceedQuotaDialog'
import { isHardQuotaError } from 'features/quotas/utils'
import { useReduxAction } from 'app/state/action'
import { dryRunDuplicateMapActionDefinition } from 'features/maps/state/duplicateMapSlice'
import { DomainOutlined, LockOutlined, MoreVertOutlined, PublicOutlined } from '@mui/icons-material'
import { createDialogOpener } from 'features/dialogs/openDialog'
import useUserPermissions from 'features/account/state/useUserPermissions'
import useIsIndividualUser from 'features/users/state/useIsIndividualUser'
import { useAppSelector } from 'app/state'
import { useMeUser } from 'features/users/hooks/useMeUser'
import { getRoleInfo } from 'features/users/model/user'
import { UserRole } from 'features/users'
import CardMiniIcon, { CardMiniIconProps } from 'features/common/ui/Cards/CardMiniIcon'
import { AiAgent } from '@carto/meridian-ds/custom-icons'
import { FeatureFlag } from 'app/types'
import { useFeatureFlag } from 'app/ui/useFeatureFlag'
import { useSettingsCartoAI } from 'features/account/hooks/useSettingsCartoAI'

const openBuilderShareDialog = createDialogOpener(
  () => import('features/builder/ui/BuilderPrivate/BuilderShareDialog/BuilderShareDialog')
)
const openMapDeleteDialog = createDialogOpener(() => import('./MapDeleteDialog'))

const MapCardAgentIcon = styled(CardMiniIcon)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacingValue * 2,
  left: theme.spacingValue * 2,
  border: `1px solid ${theme.palette.divider}`,
  boxSizing: 'content-box'
}))

interface MapCardHeaderProps {
  map: Map | DemoMap
  onCreateFromTemplate?: () => void
  displayActions?: boolean
}

function MapCardHeader({ map, onCreateFromTemplate, displayActions = true }: MapCardHeaderProps) {
  const intl = useIntl()
  const theme = useTheme()
  const user = useMeUser()
  const userRole = getRoleInfo(user).role

  const { isSharedMap, isPublicMap, isEditable, isMapOwner } = useMapSharingInfo(map)
  const { sharingMapsAvailable } = useUserPermissions()
  const isIndividual = useIsIndividualUser()
  const isPrivateMap = !isSharedMap && !isPublicMap
  const isGenAIV1 = useFeatureFlag(FeatureFlag.GenAIV1)

  const { settingsCartoAI } = useSettingsCartoAI()
  const isCartoAIEnabled = settingsCartoAI?.enabled
  const [openDuplicate, setOpenDuplicate] = useState<boolean>(false)
  const disableDuplicating = useAppSelector((state) => state.maps.duplicatingMapId !== null)
  const dryRunDuplicateAction = useReduxAction(dryRunDuplicateMapActionDefinition)
  const { duplicatingMapId, duplicateMapConflicts } = useDuplicateMap()
  const shouldOpenDialog = duplicatingMapId === map.id && duplicateMapConflicts
  const showActions = userRole !== UserRole.Guest && userRole !== UserRole.Viewer
  const isDemoMap = 'exists' in map

  const [openEdit, setOpenEdit] = useState<boolean>(false)

  const { mapsQuota } = useAccountQuotas()
  const hardQuotaModalController = useHardQuotaModalController(mapsQuota)
  const isSharedDisable = isPrivateMap && Boolean(!map.title)
  const sharingAvailable = sharingMapsAvailable || !isIndividual

  const actions = useMemo(
    () => [
      {
        id: MapActions.CreateFromTemplate,
        label: intl.formatMessage({ id: 'maps.cards.actions.editThisDemo' }),
        hidden: !isDemoMap || (map as DemoMap).exists
      },
      {
        id: MapActions.Edit,
        label: intl.formatMessage({ id: 'maps.cards.actions.edit' }),
        hidden: !isEditable
      },
      {
        id: MapActions.Share,
        label: intl.formatMessage({ id: 'maps.cards.actions.share' }),
        hidden: !isMapOwner || !sharingAvailable,
        disabled: isSharedDisable,
        tooltipId: isSharedDisable ? 'builder.navBar.shareDisabledTooltip' : undefined
      },
      {
        id: MapActions.Duplicate,
        label: intl.formatMessage({ id: 'builder.actions.duplicate.title' }),
        hidden: isDemoMap && !(map as DemoMap).exists,
        disabled: disableDuplicating
      },
      {
        id: MapActions.Delete,
        label: intl.formatMessage({ id: 'maps.cards.actions.delete' }),
        destructive: true,
        hidden: !isMapOwner
      }
    ],
    [intl, isDemoMap, map, isEditable, isMapOwner, sharingAvailable, isSharedDisable, disableDuplicating]
  )

  useEffect(() => {
    shouldOpenDialog && setOpenDuplicate(true)
  }, [shouldOpenDialog, setOpenDuplicate])

  const handleClickOption = (item: ContextMenuItem) => {
    if (item.id === MapActions.Delete) {
      openMapDeleteDialog({ map })
    } else if (item.id === MapActions.Edit) {
      setOpenEdit(true)
    } else if (item.id === MapActions.CreateFromTemplate) {
      onCreateFromTemplate && onCreateFromTemplate()
    } else if (item.id === MapActions.Duplicate && map?.id) {
      if (hardQuotaModalController.additionWouldExceedQuota()) {
        hardQuotaModalController.open()
        return
      }
      dryRunDuplicateAction.invoke(map.id)
    } else if (item.id === MapActions.Share && map?.id) {
      openBuilderShareDialog({
        mapId: map?.id,
        showPublishingStatus: false
      })
    }
  }

  const handleCloseEdit = () => {
    setOpenEdit(false)
  }

  const handleCloseDuplicate = () => {
    setOpenDuplicate(false)
  }

  const handleCloseHardQuotaModal = () => {
    hardQuotaModalController.props.onCloseModal()
    dryRunDuplicateAction.dismissResult()
  }

  useEffect(() => {
    if (isHardQuotaError(dryRunDuplicateAction.error)) {
      hardQuotaModalController.open()
    }
  })

  let miniIconProps: CardMiniIconProps | undefined

  if (isSharedMap) {
    miniIconProps = {
      icon: <DomainOutlined aria-label="Shared with organization" />,
      tooltip: true,
      tooltipLabelId: 'maps.cards.tooltips.sharedMap',
      palette: 'secondary'
    }
  } else if (isPublicMap) {
    miniIconProps = {
      icon: <PublicOutlined aria-label="Public map" />,
      tooltip: true,
      tooltipLabelId: 'maps.cards.tooltips.publicMap',
      palette: 'primary'
    }
  } else if (isPrivateMap) {
    miniIconProps = {
      icon: <LockOutlined aria-label="Private map" />,
      tooltip: true,
      tooltipLabelId: 'maps.cards.tooltips.privateMap',
      palette: 'default'
    }
  }

  const showAgentIcon = isGenAIV1 && isCartoAIEnabled && (map?.agentEnabled || map?.agent?.token)
  return (
    <>
      {showAgentIcon && (
        <MapCardAgentIcon
          icon={<AiAgent aria-label="AI" />}
          tooltip={true}
          tooltipLabelId={'maps.cards.tooltips.agentEnabled'}
          palette={'success'}
        />
      )}
      <CardBasicInformation
        minHeight={theme.spacing(10)}
        title={map.title || intl.formatMessage({ id: 'maps.default.title' })}
        miniIconProps={miniIconProps}
        action={
          showActions && displayActions ? (
            <ContextMenuIcon
              placement="bottom-end"
              aria-label="map-actions-menu"
              onClickItem={handleClickOption}
              menuItems={actions}
            >
              <MoreVertOutlined fill={theme.palette.text.secondary} />
            </ContextMenuIcon>
          ) : null
        }
      />
      <MapCardDialogWrapper
        map={map}
        isEditable={isEditable}
        openDuplicate={openDuplicate}
        handleCloseDuplicate={handleCloseDuplicate}
        openEdit={openEdit}
        handleCloseEdit={handleCloseEdit}
        handleCloseHardQuotaModal={handleCloseHardQuotaModal}
        hardQuotaModalController={hardQuotaModalController}
      />
    </>
  )
}

export default memo(MapCardHeader)
